import React, { type SyntheticEvent, useEffect } from "react";

import { useApi } from "../../Api/useApi.ts";
import { useMutation } from "@tanstack/react-query";

import ReactCrop, {
    centerCrop,
    makeAspectCrop,
    type Crop,
    type PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { CropIcon, ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "./button.tsx";
import { Slider } from "./slider.tsx";
import { Separator } from "./separator.tsx";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs.tsx";
import { Dialog, DialogContent, DialogFooter, DialogClose } from "./dialog.tsx";

import { useFormEditorContext } from "../../Contexts/FormEditorContext.jsx";

// Constants for canvas operations
const TO_RADIANS = Math.PI / 180;

// Helper function for debounced effects
function useDebounceEffect(
    fn: () => void,
    waitTime: number,
    deps: React.DependencyList = [],
) {
    useEffect(() => {
        const t = setTimeout(() => {
            fn();
        }, waitTime);

        return () => {
            clearTimeout(t);
        };
    }, deps);
}

// Canvas preview function
async function canvasPreview(
    image: HTMLImageElement,
    canvas: HTMLCanvasElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0,
) {
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("No 2d context");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Use device pixel ratio for better display on high-DPI screens
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    ctx.save();

    // 5) Move the crop origin to the canvas origin (0,0)
    ctx.translate(-cropX, -cropY);
    // 4) Move the origin to the center of the original position
    ctx.translate(centerX, centerY);
    // 3) Rotate around the origin
    ctx.rotate(rotate * TO_RADIANS);
    // 2) Scale the image
    ctx.scale(scale, scale);
    // 1) Move the center of the image to the origin (0,0)
    ctx.translate(-centerX, -centerY);

    ctx.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
    );

    ctx.restore();

    return canvas;
}

interface ImageCropModalProps {
    isOpen: boolean;
    setShowImageCropModal: (show: boolean) => void;
    selectedFile: File | null;
    setSelectedFile: (file: File | null) => void;
    onCroppedImageChange?: (croppedImage: string) => void;
    setBrandingImage?: (image: string) => void;
}

export const ImageCropModal = ({
    isOpen,
    setShowImageCropModal,
    selectedFile,
    setSelectedFile,
    onCroppedImageChange,
    setBrandingImage,
}: ImageCropModalProps) => {
    // References and state
    const { form } = useFormEditorContext();
    const id = form?.theme?.id;
    const api = useApi();

    const imgRef = React.useRef<HTMLImageElement | null>(null);
    const previewCanvasRef = React.useRef<HTMLCanvasElement>(null);

    const [imgSrc, setImgSrc] = React.useState<string>("");
    const [crop, setCrop] = React.useState<Crop>();
    const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
    const [scale, setScale] = React.useState<number>(1);
    const [currentAspect, setCurrentAspect] = React.useState<string>("normal");
    const [isUploading, setIsUploading] = React.useState<boolean>(false);

    // Create object URL when selectedFile changes
    React.useEffect(() => {
        if (selectedFile) {
            const objectUrl = URL.createObjectURL(selectedFile);
            setImgSrc(objectUrl);

            // Clean up the object URL when component unmounts or when selectedFile changes
            return () => URL.revokeObjectURL(objectUrl);
        }
    }, [selectedFile]);

    const uploadMutation = useMutation({
        mutationFn: async (form) => {
            const response = await api.files.uploadFile(form, id, true);
            setBrandingImage?.(response?.data?.url);
            setIsUploading(false);
        },
        onError: (error) => {
            console.error("Could not upload image", error);
            setIsUploading(false);
        },
    });

    // Reset crop when aspect ratio changes
    React.useEffect(() => {
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const aspect = getAspectValue(currentAspect);
            setCrop(centerAspectCrop(width, height, aspect));
        }
    }, [currentAspect]);

    function getAspectValue(aspectRatio: string): number {
        switch (aspectRatio) {
            case "1:1":
                return 1;
            case "2:1":
                return 2;
            default:
                return 1;
        }
    }

    function handleAspectChange(value: string) {
        const aspectMap = {
            normal: "1:1",
            advanced: "2:1",
        };
        setCurrentAspect(aspectMap[value]);
    }

    function onImageLoad(e: SyntheticEvent<HTMLImageElement>) {
        const { width, height } = e.currentTarget;
        const aspect = getAspectValue(currentAspect);
        setCrop(centerAspectCrop(width, height, aspect));
    }

    // Add debounce effect to update canvas when crop, scale, or other parameters change
    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                // Generate the preview canvas with proper scaling
                await canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    0, // No rotation in this component
                );
            }
        },
        100,
        [completedCrop, scale],
    );

    function handleZoom(value: number[]) {
        // Convert slider value (0-100) to scale (0.5-3)
        const newScale = 0.5 + (value[0] / 100) * 2.5;
        setScale(newScale);

        // We need to update the crop dimensions to maintain the same visual area
        if (imgRef.current && crop) {
            // When zooming, we want to keep the same crop region
            // The canvas preview will handle the proper scaling
        }
    }

    async function onCrop() {
        try {
            if (!completedCrop || !previewCanvasRef.current) {
                return;
            }

            // Get the canvas data URL with proper scaling applied
            const croppedImageUrl = previewCanvasRef.current.toDataURL(
                "image/png",
                1.0,
            );

            if (onCroppedImageChange) {
                onCroppedImageChange(croppedImageUrl);
            }

            const form = new FormData();
            const blob = await fetch(croppedImageUrl).then((res) => res.blob());
            const file = new File([blob], selectedFile?.name || "image.png", {
                type: selectedFile?.type || "image/png",
            });
            form.append("file", file);
            setIsUploading(true);
            // @ts-ignore
            uploadMutation.mutate(form);

            setShowImageCropModal(false);
        } catch (error) {
            console.error("Error cropping image:", error);
        }
    }

    function handleClose() {
        setShowImageCropModal(false);
    }

    function handleCancel() {
        setSelectedFile(null);
        setShowImageCropModal(false);
    }

    // Calculate slider default value from scale
    const sliderDefaultValue = Math.round(((scale - 0.5) / 2.5) * 100);

    // Don't render the dialog content if there's no selected file
    if (!selectedFile || !imgSrc) {
        return null;
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="p-0 top-[50%] w-[600px] gap-0">
                <div className="flex flex-col p-3">
                    <div className="flex items-center gap-1">
                        <CropIcon className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-medium text-gray-700">
                            Crop Image
                        </h2>
                    </div>
                    <h3 className="text-xs">
                        Adjust the grid size to crop your logo for the best fit
                        in the frame.
                    </h3>
                </div>
                <Separator />
                <div className="w-full px-3 pt-4">
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={getAspectValue(currentAspect)}
                        className="w-full"
                    >
                        <img
                            ref={imgRef}
                            alt="Crop me"
                            src={imgSrc}
                            onLoad={onImageLoad}
                            className="object-cover w-full h-full"
                            style={{ transform: `scale(${scale})` }}
                        />
                    </ReactCrop>

                    {/* Hidden canvas for preview - it will be used to generate the final image */}
                    <canvas
                        ref={previewCanvasRef}
                        style={{
                            display: "none", // Hide the canvas
                            width: completedCrop?.width || 0,
                            height: completedCrop?.height || 0,
                        }}
                    />
                </div>
                <div className="flex items-center gap-2 px-3 pt-1 pb-3.5">
                    <Tabs
                        defaultValue="normal"
                        onValueChange={handleAspectChange}
                    >
                        <TabsList className="w-[192px] h-9">
                            <TabsTrigger
                                className="w-[92px] h-7"
                                value="normal"
                            >
                                1:1
                            </TabsTrigger>
                            <TabsTrigger
                                className="w-[92px] h-7"
                                value="advanced"
                            >
                                2:1
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <div className="flex items-center bg-[#F7F8F9] rounded-[8px] py-3 px-2 w-full gap-2">
                        <ZoomOut className="w-4 h-4 text-gray-500" />
                        <Slider
                            defaultValue={[sliderDefaultValue]}
                            value={[sliderDefaultValue]}
                            min={0}
                            max={100}
                            step={1}
                            className="w-full"
                            onValueChange={handleZoom}
                        />
                        <ZoomIn className="w-4 h-4 text-gray-500" />
                    </div>
                </div>
                <Separator />
                <DialogFooter className="justify-center p-3">
                    <DialogClose asChild>
                        <Button
                            size="sm"
                            type="reset"
                            className="text-red-500 hover:text-red-500 w-fit hover:bg-transparent"
                            variant="ghost"
                            onClick={handleCancel}
                        >
                            Discard
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        size="sm"
                        className="text-xs font-normal h-9 w-fit"
                        disabled={
                            !completedCrop?.width ||
                            !completedCrop?.height ||
                            isUploading
                        }
                        onClick={onCrop}
                    >
                        {isUploading ? "Processing..." : "Crop image"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Helper function to center the crop
export function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: "%",
                width: 50,
                height: 50,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
}
